<script setup lang="ts">
/**
 * LandingArchiveBar — 깔때기 마지막 단(`flow='archive'`) 축의 하단 바.
 *
 * 리포트 보관함은 "여섯 번째 기능"이 아니라 **위 분석들이 만든 결과가 쌓이는 곳**이다. 카드로 그리면
 * 분석 축과 같은 위계로 읽혀 흐름이 끊기므로, 카드보다 낮은 위계의 가로 바 한 줄로 그린다
 * (헤더 밴드·고스트 숫자·불릿 없음 — 저채도 배경 + 아이콘 + 한 줄 설명 + 칩).
 *
 * 축 선언은 LandingAxes 가 `axesByFlow('archive')` 로 골라 내려준다 — 여기서 key 를 알지 못한다.
 * 이동·게이트는 페이지 소유 — `LandingGoTarget` 만 올린다.
 */
import { Archive, ChevronRight, ClipboardList, ListChecks, Target } from 'lucide-vue-next';
import type { LucideIcon } from 'lucide-vue-next';

import type { LandingAxis, LandingAxisIcon, LandingGoTarget } from '#/data/analysis/landingAxes';

defineProps<{ axis: LandingAxis }>();

defineEmits<{
  /** open 축 클릭 — 페이지가 게이트 판정 후 대표 route 로 이동. */
  go: [target: LandingGoTarget];
}>();

/** 아이콘 키 → lucide 컴포넌트(LandingAxisCard 와 동일 매핑 — 축 선언이 어떤 아이콘이든 받게 둔다). */
const ICON_MAP: Record<LandingAxisIcon, LucideIcon> = {
  clipboard: ClipboardList,
  target: Target,
  'list-checks': ListChecks,
  archive: Archive,
};
</script>

<template>
  <component
    :is="axis.status === 'open' ? 'button' : 'div'"
    :type="axis.status === 'open' ? 'button' : undefined"
    class="ws-arch"
    :class="{ 'ws-arch--go': axis.status === 'open' }"
    @click="axis.status === 'open' && $emit('go', { name: axis.name, route: axis.route })"
  >
    <span class="ws-arch-icon">
      <component :is="ICON_MAP[axis.icon]" :size="20" />
    </span>

    <span class="ws-arch-text">
      <span class="ws-arch-name">{{ axis.name }}</span>
      <span class="ws-arch-one">{{ axis.one }}</span>
    </span>

    <span class="ws-arch-chip">{{ axis.status === 'soon' ? '준비중' : axis.tag }}</span>

    <span v-if="axis.status === 'open'" class="ws-arch-arrow"><ChevronRight :size="18" /></span>
  </component>
</template>

<style scoped>
.ws-arch {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  text-align: left;

  /* 카드(--bg 흰색)보다 한 단 낮은 위계 — 저채도 배경 + 실선 테두리 없이 점선 느낌의 옅은 선 */
  background: var(--bg-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-card);
}

.ws-arch--go {
  cursor: pointer;
  transition:
    border-color 0.16s,
    background 0.16s;
}

.ws-arch--go:hover {
  background: var(--bg);
  border-color: var(--brand);
}

.ws-arch--go:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

.ws-arch-icon {
  display: grid;
  place-content: center;
  width: 36px;
  height: 36px;
  flex: 0 0 auto;
  border-radius: 10px;
  background: var(--bg-3);
  color: var(--brand);
}

.ws-arch-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1 1 auto;
}

.ws-arch-name {
  font-size: 15.5px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--brand-ink);
}

.ws-arch-one {
  font-size: 13.5px;
  line-height: 1.6;
  color: var(--body);
}

.ws-arch-chip {
  display: none; /* 좁은 화면에서는 한 줄 설명이 이미 같은 정보를 담는다 */
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1.4;
  color: var(--muted);
  border: 1.5px solid var(--line);
  border-radius: 999px;
  padding: 3px 10px;
  flex: 0 0 auto;
  white-space: nowrap;
}

@media (width >= 600px) {
  .ws-arch-chip {
    display: inline-block;
  }
}

.ws-arch-arrow {
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  color: var(--muted);
}

.ws-arch--go:hover .ws-arch-arrow {
  color: var(--brand);
}
</style>
